import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconAlignLeft,
  IconArrowLeft,
  IconCheck,
  IconDeviceFloppy,
  IconExternalLink,
  IconFileText,
  IconLock,
  IconMessage,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { DIFF_LINE_STYLES, diffLines } from '../../helpers/lineDiff';
import useFetch from '../../helpers/useFetch';
import Spinner from '../Spinner';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';
import DocumentBrowserStepsCard from './DocumentBrowserStepsCard';
import {
  NoVectorBadge,
  PIPELINE_STAGES,
  StageBadge,
  StatusBadge,
  TitleEditor,
  TitleLockIcon,
  UnitStatusBadge,
  formatDate,
  formatMs,
  interpretIngestError,
  stripProtocol,
} from './shared';

const STEP_DOT_STYLES = {
  done: { background: 'var(--mantine-color-blue-6)' },
  failed: { background: 'var(--mantine-color-red-5)' },
  running: { background: 'var(--mantine-color-body)', border: '2px solid var(--mantine-color-blue-6)' },
  pending: { background: 'var(--mantine-color-gray-1)', border: '1px solid var(--mantine-color-gray-3)' },
  skipped: { background: 'var(--mantine-color-gray-1)', border: '1px solid var(--mantine-color-gray-3)' },
};

export const jobSteps = (job) => {
  const timings = job.timings || {};
  const runningIdx = PIPELINE_STAGES.indexOf(job.stage);
  let failedIdx = -1;
  if (job.stage === 'failed') {
    // A failed job records no duration for the stage it died in — the same contract
    // IngestQueueScreen.timingSegments encodes ("the stage it died in has no recorded
    // time — a red tail marks where it stopped"). So every timed stage completed and
    // the failure sits on the one after the last of them, clamped to the final stage
    // for a job that got all the way through before failing.
    let lastTimedIdx = -1;
    PIPELINE_STAGES.forEach((name, i) => {
      if (timings[name] != null) lastTimedIdx = i;
    });
    failedIdx = Math.min(lastTimedIdx + 1, PIPELINE_STAGES.length - 1);
  }
  return PIPELINE_STAGES.map((name, i) => {
    const ms = timings[name];
    let state = 'pending';
    // A stage that took 0 ms is still a completed stage — match IngestQueueScreen's `!= null` test.
    if (job.stage === 'ready') state = ms != null ? 'done' : 'skipped';
    else if (runningIdx >= 0) state = i < runningIdx ? 'done' : i === runningIdx ? 'running' : 'pending';
    else if (failedIdx >= 0) state = i < failedIdx ? 'done' : i === failedIdx ? 'failed' : 'pending';
    const label =
      state === 'done'
        ? formatMs(ms) || '—'
        : state === 'skipped'
          ? 'skipped'
          : state === 'running'
            ? 'running…'
            : state === 'failed'
              ? ms != null
                ? `${formatMs(ms)} · failed`
                : 'failed'
              : '—';
    return { name, state, label };
  });
};

const fmtDay = (value) => (value ? new Date(value).toLocaleDateString() : '…');

const GOOGLE_DOC_EXPORT = /^https:\/\/docs\.google\.com\/document\/d\/([^/]+)\/export/;

// A Google Docs source is stored as its PDF export URL; link back to the editable doc.
const sourceLink = (sourceUrl) => {
  const googleDoc = GOOGLE_DOC_EXPORT.exec(sourceUrl || '');
  if (googleDoc) return { href: `https://docs.google.com/document/d/${googleDoc[1]}/edit`, label: 'Google Doc' };
  try {
    return { href: sourceUrl, label: new URL(sourceUrl).host };
  } catch {
    return null;
  }
};

const headingLabel = (headingPath) => (Array.isArray(headingPath) ? headingPath.join(' › ') : headingPath || '');

const DocumentDetailScreen = ({ apiBase, scope, refreshCounts, isSuperAdmin, docId, onBack }) => {
  const fetch = useFetch();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('content');
  const [contentDraft, setContentDraft] = useState('');
  const [contentDirty, setContentDirty] = useState(false);
  const [rawView, setRawView] = useState(false);
  const [resyncNotice, setResyncNotice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [diffOpenId, setDiffOpenId] = useState(null);
  const [diffRows, setDiffRows] = useState(null);
  const [diffLoadingId, setDiffLoadingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Deleting calls onBack(), which unmounts this screen while the request is still
  // settling — the trailing setState calls have to check first.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Only the first load of a document may replace the screen with the error page. The
  // reloads chained after a successful save/publish/restore must not: the mutation did
  // land, so swapping the editor for "Failed to load document" would report a success as
  // a failure. Kept in a ref rather than reading `data` so `load` stays stable.
  const hasLoaded = useRef(false);

  const load = useCallback(
    (keepDraft) =>
      fetch(`${apiBase}/documents/${docId}`)
        .then((res) => {
          hasLoaded.current = true;
          setLoadError(null);
          setData(res);
          if (!keepDraft) {
            setContentDraft(res.document?.canonicalMd || '');
            setContentDirty(false);
          }
        })
        .catch((err) => {
          const message = err.message || 'Failed to load document';
          if (hasLoaded.current) {
            notifications.show({ title: 'Could not refresh the document', message, color: 'danger' });
          } else {
            setLoadError(message);
          }
        }),
    [apiBase, docId, fetch]
  );

  useEffect(() => {
    // A different document starts from scratch — it gets its own full-page error rather
    // than inheriting the previous document's successful load.
    hasLoaded.current = false;
    setData(null);
    setLoadError(null);
    load();
  }, [load]);

  if (loadError) {
    return (
      <Alert color="danger" title="Failed to load document" mt={14}>
        <Text size="sm">{loadError}</Text>
        <Button size="xs" variant="default" mt="sm" onClick={onBack}>
          Back to documents
        </Button>
      </Alert>
    );
  }
  if (!data) return <Spinner />;

  const doc = data.document;
  const units = data.units || [];
  const versions = data.versions || [];
  const jobs = data.jobs || [];
  const sortedUnits = [...units].sort((a, b) => (a.seq || 0) - (b.seq || 0));
  const noVectorCount = units.filter((u) => !u.hasEmbedding).length;
  const liveCount = doc.reviewStatus === 'published' ? units.filter((u) => u.reviewStatus === 'published').length : 0;
  const isUrlDoc = ['url', 'html'].includes(doc.sourceType);
  const isSyncable = isUrlDoc || doc.sourceType === 'pdf';
  const showSyncLine = isSyncable && doc.processingMethod === 'raw_sync';
  const lastChange = versions.find((v) => v.changeNote === 'resync');
  const source = sourceLink(doc.sourceUrl);
  const canRefetch = doc.sourceType !== 'manual';

  // Resolves true once saved, so the rename editor closes only on success.
  const patchDoc = (patch) =>
    fetch(`${apiBase}/documents/${docId}`, { method: 'PUT', body: JSON.stringify(patch) })
      .then(({ document }) => {
        setData((prev) => ({ ...prev, document }));
        if (patch.autoResync) setResyncNotice(false);
        return true;
      })
      .catch((err) => {
        notifications.show({ title: 'Update failed', message: err.message, color: 'danger' });
        return false;
      });

  const saveTitle = (title) => patchDoc({ title }).then((saved) => saved && setEditingTitle(false));

  const syncNow = () => {
    setSyncing(true);
    return fetch(`${apiBase}/documents/${docId}/resync`, { method: 'POST', body: JSON.stringify({}) })
      .then(() => {
        notifications.show({
          title: 'Sync queued',
          message: 'The source is checked in the background.',
          color: 'blue',
        });
        // One refetch picks up "Last checked" once the job has had a moment to run.
        setTimeout(() => mounted.current && load(true), 3000);
      })
      .catch((err) => notifications.show({ title: 'Sync failed', message: err.message, color: 'danger' }))
      .finally(() => setSyncing(false));
  };

  const setStatus = (reviewStatus) =>
    fetch(`${apiBase}/documents/${docId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ reviewStatus }),
    }).then(({ document }) => {
      setData((prev) => ({ ...prev, document }));
      return load(true);
    });

  const saveContent = () => {
    setSaving(true);
    const wasResync = doc.autoResync;
    fetch(`${apiBase}/documents/${docId}`, { method: 'PUT', body: JSON.stringify({ canonicalMd: contentDraft }) })
      .then(({ document }) => {
        setData((prev) => ({ ...prev, document }));
        setContentDirty(false);
        if (wasResync && !document.autoResync) setResyncNotice(true);
        notifications.show({
          title: 'Changes saved — re-processing queued',
          message: 'The edit re-chunks and re-embeds in the background. The previous version stays live meanwhile.',
          color: 'success',
        });
        return load(true);
      })
      .catch((err) => notifications.show({ title: 'Save failed', message: err.message, color: 'danger' }))
      .finally(() => setSaving(false));
  };

  const reingest = (fromStored) =>
    fetch(`${apiBase}/documents/${docId}/reingest`, {
      method: 'POST',
      body: JSON.stringify(fromStored ? { fromStored: true } : {}),
    })
      .then(() =>
        notifications.show(
          fromStored
            ? {
                title: 'Re-chunk queued',
                message:
                  'Stored content will be re-chunked and re-embedded without re-fetching the source. The previous version stays live meanwhile.',
                color: 'blue',
              }
            : {
                title: 'Re-ingest queued',
                message:
                  'Re-fetching from source — a new pipeline run appears in the Ingest Queue. The previous version stays live meanwhile.',
                color: 'blue',
              }
        )
      )
      .catch((err) => notifications.show({ title: 'Request failed', message: err.message, color: 'danger' }));

  const askPublish = () =>
    setConfirm({
      title: `Publish “${doc.title}”`,
      body: `${units.length} units will go live for the AI assistant immediately. Draft and validated content is never served — publishing is the review gate.`,
      note: 'You can unpublish at any time; the corpus keeps every version.',
      cta: `Publish ${units.length} units`,
      action: () =>
        setStatus('published').then(() =>
          notifications.show({
            title: 'Document published',
            message: `${units.length} units are now live for the assistant.`,
            color: 'success',
          })
        ),
    });

  const askUnpublish = () =>
    setConfirm({
      title: `Unpublish “${doc.title}”`,
      body: `${liveCount} live units will stop being served immediately. The assistant falls back to the remaining published corpus.`,
      note: 'The document goes back to “draft” — nothing is deleted.',
      cta: 'Unpublish',
      color: 'danger',
      action: () =>
        setStatus('draft').then(() =>
          notifications.show({
            title: 'Document unpublished',
            message: 'Units removed from serving. Content stays in the corpus as a draft.',
            color: 'blue',
          })
        ),
    });

  const askDelete = () =>
    setConfirm({
      title: `Delete “${doc.title}”`,
      body: `This permanently removes the document, its ${units.length} units and their vectors from the corpus. The assistant will no longer cite it. This cannot be undone.`,
      cta: 'Delete document',
      color: 'danger',
      action: () =>
        fetch(`${apiBase}/documents/${docId}`, { method: 'DELETE' }).then(() => {
          notifications.show({
            title: 'Document deleted',
            message: `“${doc.title}” and its units were removed from the corpus.`,
            color: 'danger',
          });
          refreshCounts();
          onBack();
        }),
    });

  const closeConfirm = () => {
    setConfirm(null);
    setConfirmError(null);
  };

  const runConfirm = () => {
    setConfirmLoading(true);
    setConfirmError(null);
    confirm
      .action()
      .then(() => {
        if (mounted.current) closeConfirm();
      })
      .catch((err) => {
        if (mounted.current) setConfirmError(err.message || 'Request failed');
      })
      .finally(() => {
        if (mounted.current) setConfirmLoading(false);
      });
  };

  const toggleDiff = (versionId) => {
    if (diffOpenId === versionId) {
      setDiffOpenId(null);
      setDiffRows(null);
      return;
    }
    setDiffLoadingId(versionId);
    fetch(`${apiBase}/documents/${docId}/versions/${versionId}`)
      .then(({ version }) => {
        setDiffRows(diffLines(version.canonicalMd || '', doc.canonicalMd || ''));
        setDiffOpenId(versionId);
      })
      .catch((err) => notifications.show({ title: 'Failed to load version', message: err.message, color: 'danger' }))
      .finally(() => setDiffLoadingId(null));
  };

  const restoreVersion = (v) => {
    setRestoringId(v.id);
    fetch(`${apiBase}/documents/${docId}/versions/${v.id}/restore`, { method: 'POST', body: JSON.stringify({}) })
      .then(() => {
        notifications.show({
          title: 'Version restored',
          message: `v${v.version} content queued for re-processing — a new version was created.`,
          color: 'success',
        });
        return load();
      })
      .catch((err) => notifications.show({ title: 'Restore failed', message: err.message, color: 'danger' }))
      .finally(() => setRestoringId(null));
  };

  return (
    <div style={{ marginTop: 14 }}>
      <style>{'@keyframes kbPulse{0%,100%{opacity:1}50%{opacity:.35}}'}</style>

      <Card withBorder radius="sm" padding="md">
        <Group gap={10} wrap="wrap">
          <ActionIcon variant="subtle" color="gray" onClick={onBack}>
            <IconArrowLeft size={18} stroke={1.5} />
          </ActionIcon>
          {editingTitle ? (
            <TitleEditor value={doc.title} onSave={saveTitle} onCancel={() => setEditingTitle(false)} />
          ) : (
            <>
              <Text fz="lg" fw={600}>
                {doc.title}
              </Text>
              <ActionIcon variant="subtle" color="gray" aria-label="Rename" onClick={() => setEditingTitle(true)}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon>
            </>
          )}
          {doc.titleLocked && <TitleLockIcon />}
          <StatusBadge status={doc.reviewStatus} />
          {noVectorCount > 0 && (
            <Tooltip
              label="These units are served keyword-only until the background embedding job finishes. Nothing is offline."
              multiline
              maw={260}
              withArrow
            >
              <Badge variant="dot" color="gray" size="sm">
                {noVectorCount} {noVectorCount === 1 ? 'unit' : 'units'} without vector
              </Badge>
            </Tooltip>
          )}
          <div style={{ flex: 1 }} />
          {doc.reviewStatus === 'published' ? (
            <Button size="sm" variant="default" onClick={askUnpublish}>
              Unpublish
            </Button>
          ) : (
            <Button size="sm" leftSection={<IconCheck size={15} stroke={2} />} onClick={askPublish}>
              Publish
            </Button>
          )}
          {canRefetch && (
            <Button
              size="sm"
              variant="default"
              leftSection={<IconRefresh size={15} stroke={1.5} />}
              onClick={() => reingest(false)}
            >
              Re-fetch source
            </Button>
          )}
          <Button size="sm" variant="default" onClick={() => reingest(true)}>
            Re-chunk
          </Button>
          <ActionIcon variant="subtle" color="danger" onClick={askDelete}>
            <IconTrash size={17} stroke={1.5} />
          </ActionIcon>
        </Group>
        <Group gap={18} mt={14} wrap="wrap">
          <Switch
            size="sm"
            label="Agent enabled"
            checked={!!doc.agentEnabled}
            onChange={(e) => patchDoc({ agentEnabled: e.currentTarget.checked })}
          />
          <Tooltip
            label={
              doc.processingMethod === 'llm_structured'
                ? 'AI-structured documents are excluded from the nightly raw re-import — re-process manually via Re-fetch source.'
                : 'Re-imports the source nightly when its content changed.'
            }
            multiline
            maw={280}
            withArrow
          >
            <div>
              <Switch
                size="sm"
                label="Auto-resync nightly"
                checked={!!doc.autoResync}
                disabled={!isSyncable || doc.processingMethod === 'llm_structured'}
                onChange={(e) => patchDoc({ autoResync: e.currentTarget.checked })}
              />
            </div>
          </Tooltip>
          {doc.processingMethod === 'llm_structured' && (
            <Tooltip
              label="The content was rewritten into clean structure by AI at ingest — the raw extraction is viewable in the Content tab."
              multiline
              maw={280}
              withArrow
            >
              <Badge variant="light" color="info" size="sm">
                AI-structured
              </Badge>
            </Tooltip>
          )}
          <Divider orientation="vertical" style={{ height: 18, alignSelf: 'center' }} />
          {doc.priority > 0 && (
            <Badge variant="default" size="sm" tt="none">
              priority {doc.priority}
            </Badge>
          )}
          {(doc.activeFrom || doc.activeTo) && (
            <Badge variant="default" size="sm" tt="none">
              active {fmtDay(doc.activeFrom)} → {fmtDay(doc.activeTo)}
            </Badge>
          )}
          {(doc.tags || []).map((tag) => (
            <Badge key={tag} variant="default" size="sm" tt="none">
              {tag}
            </Badge>
          ))}
          {doc.sourceUrl && /^https?:\/\//i.test(doc.sourceUrl) && (
            <Anchor
              href={doc.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              fz={12}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconExternalLink size={13} stroke={1.5} />
              {stripProtocol(doc.sourceUrl)}
            </Anchor>
          )}
          <div style={{ flex: 1 }} />
          <Text fz={12} c="dimmed">
            <Text span fz={12} fw={600} c="gray.7">
              {units.length} units
            </Text>{' '}
            · {liveCount} live · updated {formatDate(doc.updatedAt)}
          </Text>
        </Group>
        {showSyncLine && (
          <Group
            gap="6px 14px"
            mt={12}
            px={10}
            py={8}
            wrap="wrap"
            fz={12.5}
            c="dimmed"
            style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 'var(--mantine-radius-sm)' }}
          >
            {source && (
              <span>
                Source:{' '}
                <Anchor href={source.href} target="_blank" rel="noopener noreferrer" fz={12.5} fw={600}>
                  {source.label} ↗
                </Anchor>
              </span>
            )}
            <span>
              Last checked{' '}
              <Text span fz={12.5} fw={600} c="var(--mantine-color-text)">
                {doc.lastCheckedAt ? DateTime.fromISO(doc.lastCheckedAt).toRelative() : 'never'}
              </Text>
            </span>
            {lastChange && (
              <span>
                Last changed{' '}
                <Text span fz={12.5} fw={600} c="var(--mantine-color-text)">
                  {formatDate(lastChange.createdAt)}
                </Text>
              </span>
            )}
            {doc.autoResync && (
              <span>
                Next{' '}
                <Text span fz={12.5} fw={600} c="var(--mantine-color-text)">
                  03:00 UTC
                </Text>
              </span>
            )}
            <div style={{ flex: 1 }} />
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} stroke={1.5} />}
              loading={syncing}
              onClick={() =>
                doc.autoResync
                  ? syncNow()
                  : setConfirm({
                      title: 'Sync from source?',
                      body: 'Auto-resync is off because this document was edited or restored by hand. Syncing replaces that content with the source if the source differs.',
                      cta: 'Sync now',
                      action: syncNow,
                    })
              }
            >
              Sync now
            </Button>
          </Group>
        )}
      </Card>

      <Tabs value={tab} onChange={(value) => value && setTab(value)} mt={12}>
        <Tabs.List>
          <Tabs.Tab value="content">Content</Tabs.Tab>
          <Tabs.Tab value="units">Units · {units.length}</Tabs.Tab>
          <Tabs.Tab value="versions">Versions</Tabs.Tab>
          <Tabs.Tab value="jobs">Jobs</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'content' && (
        <Card withBorder radius="sm" padding="md" mt={12}>
          <Group gap={10} wrap="wrap" mb={10}>
            <Text size="sm" fw={500}>
              {rawView ? 'Raw extraction (read-only)' : 'Canonical markdown'}
            </Text>
            <div style={{ flex: 1 }} />
            <Switch
              size="sm"
              label="View raw extraction"
              checked={rawView}
              onChange={(e) => setRawView(e.currentTarget.checked)}
            />
          </Group>
          {isSyncable && doc.autoResync && (
            <Group gap={6} mb={10} wrap="nowrap">
              <IconAlertTriangle size={14} stroke={1.5} color="var(--mantine-color-yellow-6)" />
              <Text fz={12} c="dimmed">
                This document re-imports nightly from its source URL. Saving a manual edit turns auto-resync <b>off</b>{' '}
                so your changes are not overwritten.
              </Text>
            </Group>
          )}
          {resyncNotice && (
            <Alert color="warning" title="Auto-resync disabled" mb={10}>
              This document now contains manual edits. Nightly re-import from the source URL was turned off — re-enable
              it from the header switch to discard local changes on the next sync.
            </Alert>
          )}
          {rawView ? (
            <>
              <Textarea value={doc.rawContent || ''} readOnly autosize minRows={12} />
              <Text fz={12} c="dimmed" mt={6}>
                Raw extraction is read-only — it is what the parser produced from the source before cleanup.
              </Text>
            </>
          ) : (
            <Textarea
              value={contentDraft}
              onChange={(e) => {
                setContentDraft(e.currentTarget.value);
                setContentDirty(true);
              }}
              autosize
              minRows={12}
            />
          )}
          <Group justify="flex-end" gap={12} mt={12}>
            <Text fz={12} c="dimmed">
              Saving creates a new version and re-chunks in the background — the previous version stays live meanwhile.
            </Text>
            <Button
              size="sm"
              leftSection={<IconDeviceFloppy size={15} stroke={1.5} />}
              disabled={!contentDirty}
              loading={saving}
              onClick={saveContent}
            >
              Save changes
            </Button>
          </Group>
        </Card>
      )}

      {tab === 'units' && (
        <Stack gap={8} mt={12}>
          {sortedUnits.length === 0 && (
            <Card withBorder radius="sm" padding="lg">
              <Stack align="center" gap={8} py={24}>
                <IconFileText size={40} stroke={1} color="var(--mantine-color-gray-5)" />
                <Text fz="lg" fw={500}>
                  No units yet
                </Text>
                <Text size="sm" c="dimmed">
                  {jobs[0]?.stage === 'failed'
                    ? 'The last ingest failed before chunking — retry it from the Ingest Queue.'
                    : 'Units appear here once the first ingest run finishes.'}
                </Text>
              </Stack>
            </Card>
          )}
          {sortedUnits.map((unit) => (
            <Paper key={unit.id} withBorder radius="sm" p="sm">
              <Group gap={8} wrap="wrap">
                <Text fz={12} c="dimmed">
                  {headingLabel(unit.headingPath)}
                </Text>
                {unit.page != null && (
                  <Badge variant="default" size="sm" tt="none">
                    p. {unit.page}
                  </Badge>
                )}
                <div style={{ flex: 1 }} />
                <Badge
                  variant="default"
                  size="sm"
                  tt="none"
                  leftSection={
                    unit.unitType === 'qa' ? (
                      <IconMessage size={12} stroke={1.5} />
                    ) : (
                      <IconAlignLeft size={12} stroke={1.5} />
                    )
                  }
                >
                  {unit.unitType}
                </Badge>
                <UnitStatusBadge status={unit.reviewStatus} documentStatus={doc.reviewStatus} />
                {!unit.hasEmbedding && <NoVectorBadge />}
                {unit.unitType === 'chunk' && (
                  <Tooltip label="Chunk text is owned by the source document" withArrow>
                    <Text fz={11} c="gray.5" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <IconLock size={13} stroke={1.5} />
                      source-owned
                    </Text>
                  </Tooltip>
                )}
              </Group>
              {unit.unitType === 'qa' && unit.question && (
                <Text fz={13} fw={600} mt={6}>
                  {unit.question}
                </Text>
              )}
              <Text fz={13} c="gray.7" mt={unit.unitType === 'qa' ? 4 : 6} lh={1.5}>
                {unit.content}
              </Text>
            </Paper>
          ))}
        </Stack>
      )}

      {tab === 'versions' && (
        <Card withBorder radius="sm" padding="xs" mt={12}>
          <Group
            gap={10}
            wrap="wrap"
            style={{ padding: '10px 12px', borderBottom: '1px solid var(--mantine-color-gray-1)' }}
          >
            <Badge variant="default" size="sm" tt="none">
              v{doc.version}
            </Badge>
            <Text size="sm" fw={500}>
              Current content
            </Text>
            <Text fz={12} c="dimmed">
              {formatDate(doc.updatedAt)}
            </Text>
            <Badge variant="light" color="info" size="sm">
              current
            </Badge>
          </Group>
          {versions.length === 0 && (
            <Text size="sm" c="dimmed" p="sm">
              No previous versions — they appear when a source change or edit re-processes the document.
            </Text>
          )}
          {versions.map((v) => {
            return (
              <Fragment key={v.id}>
                <Group
                  gap={10}
                  wrap="wrap"
                  style={{ padding: '10px 12px', borderBottom: '1px solid var(--mantine-color-gray-1)' }}
                >
                  <Badge variant="default" size="sm" tt="none">
                    v{v.version}
                  </Badge>
                  <Text size="sm" fw={500}>
                    {v.changeNote || '—'}
                  </Text>
                  <Text fz={12} c="dimmed">
                    {v.userId != null ? `user ${v.userId} · ` : ''}
                    {formatDate(v.createdAt)}
                  </Text>
                  <div style={{ flex: 1 }} />
                  <Button size="xs" variant="default" loading={diffLoadingId === v.id} onClick={() => toggleDiff(v.id)}>
                    {diffOpenId === v.id ? 'Hide diff' : 'View diff'}
                  </Button>
                  <Button size="xs" variant="default" loading={restoringId === v.id} onClick={() => restoreVersion(v)}>
                    Restore
                  </Button>
                </Group>
                {diffOpenId === v.id && diffRows && (
                  <div
                    style={{
                      padding: '12px 14px',
                      background: 'var(--mantine-color-gray-0)',
                      borderBottom: '1px solid var(--mantine-color-gray-1)',
                      fontFamily: 'var(--mantine-font-family-monospace)',
                      fontSize: 12,
                      lineHeight: 1.8,
                    }}
                  >
                    <Text fz={11} c="dimmed" mb={6}>
                      v{v.version} → v{doc.version} (current)
                    </Text>
                    {diffRows.map((line, i) => (
                      <div key={i} style={DIFF_LINE_STYLES[line.kind]}>
                        {line.text || ' '}
                      </div>
                    ))}
                  </div>
                )}
              </Fragment>
            );
          })}
        </Card>
      )}

      {tab === 'jobs' && (
        <Stack gap={8} mt={12}>
          {jobs.length === 0 && (
            <Text size="sm" c="dimmed">
              No pipeline runs yet.
            </Text>
          )}
          {jobs.map((job) => (
            <Paper key={job.id} withBorder radius="sm" p="md">
              <Group gap={10} wrap="wrap">
                {isSuperAdmin && (
                  <Text fz={13} fw={600} ff="monospace">
                    {job.id}
                  </Text>
                )}
                <StageBadge stage={job.stage} error={job.error} />
                <div style={{ flex: 1 }} />
                <Text fz={12} c="dimmed">
                  {formatDate(job.createdAt)}
                </Text>
              </Group>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 14, maxWidth: 640 }}>
                {jobSteps(job).map((step, i) => (
                  <Fragment key={step.name}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        width: 92,
                        flex: '0 0 auto',
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          ...STEP_DOT_STYLES[step.state],
                        }}
                      >
                        {step.state === 'done' && <IconCheck size={11} stroke={3} color="var(--mantine-color-white)" />}
                        {step.state === 'failed' && <IconX size={11} stroke={3} color="var(--mantine-color-white)" />}
                        {step.state === 'running' && (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: 'var(--mantine-color-blue-6)',
                              animation: 'kbPulse 1.2s ease-in-out infinite',
                            }}
                          />
                        )}
                      </div>
                      <Text fz={12} fw={500} c="gray.7">
                        {step.name}
                      </Text>
                      <Text fz={11} c="dimmed">
                        {step.label}
                      </Text>
                    </div>
                    {i < PIPELINE_STAGES.length - 1 && (
                      <div
                        style={{
                          height: 2,
                          flex: '1 1 24px',
                          minWidth: 16,
                          marginTop: 9,
                          background:
                            step.state === 'done' ? 'var(--mantine-color-blue-3)' : 'var(--mantine-color-gray-2)',
                        }}
                      />
                    )}
                  </Fragment>
                ))}
              </div>
              {job.stage === 'failed' && (
                // Gated on the stage, not on job.error, so a run that carried error text
                // but still finished can't show "didn't finish · press Retry" next to a
                // green `ready` badge — same test as IngestQueueScreen. Everyone sees the
                // merchant-friendly summary; the raw text stays behind the superadmin gate.
                <div
                  style={{
                    marginTop: 12,
                    background: 'var(--mantine-color-red-0)',
                    border: '1px solid var(--mantine-color-red-2)',
                    borderRadius: 4,
                    padding: '8px 10px',
                  }}
                >
                  <Text fz={13} fw={600} c="red.9">
                    {interpretIngestError(job.error).title}
                  </Text>
                  <Text fz={12.5} c="dimmed" mt={2}>
                    {interpretIngestError(job.error).hint}
                  </Text>
                  {isSuperAdmin && job.error && (
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)', cursor: 'pointer' }}>
                        Technical details <SuperAdminBadge show iconOnly tooltip="Raw error — super admins only" />
                      </summary>
                      <Text fz={11} ff="monospace" c="dimmed" mt={4} style={{ wordBreak: 'break-word' }}>
                        {job.error}
                      </Text>
                    </details>
                  )}
                </div>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      {/* Below the tab panels, not inside one: panels unmount, so switching tabs would drop unsaved
          steps. Last on the screen and collapsed, since it only matters when an import fails. */}
      {isUrlDoc && (
        <SuperAdminArea show={isSuperAdmin} mt={12}>
          {Array.isArray(doc.browserSteps) ? (
            <DocumentBrowserStepsCard
              key={doc.id}
              apiBase={apiBase}
              docId={docId}
              doc={doc}
              scope={scope}
              onSaved={(document) => setData((prev) => ({ ...prev, document }))}
            />
          ) : (
            <Text fz={12} c="dimmed">
              Browser steps are not available from the API yet.
            </Text>
          )}
        </SuperAdminArea>
      )}

      <Modal opened={!!confirm} onClose={closeConfirm} title={confirm?.title} size="md" centered>
        <Text size="sm">{confirm?.body}</Text>
        {confirm?.note && (
          <Text fz={12} c="dimmed" mt={8}>
            {confirm.note}
          </Text>
        )}
        {confirmError && (
          <Text size="sm" c="danger" mt={8}>
            {confirmError}
          </Text>
        )}
        <Divider my="md" />
        <Group justify="flex-end" gap={8}>
          <Button size="sm" variant="default" onClick={closeConfirm}>
            Cancel
          </Button>
          <Button size="sm" color={confirm?.color} loading={confirmLoading} onClick={runConfirm}>
            {confirm?.cta}
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default DocumentDetailScreen;
